import { ref, readonly, markRaw } from 'vue';
import { PhReceipt, PhLightning, PhGasPump, PhDrop, PhFileText, PhChalkboardTeacher, PhUsersThree, PhTable } from '@phosphor-icons/vue';

/**
 * useAssistantDock — estado del asistente flotante (AssistantDock, global en App.vue)
 * y la configuración de cada MÓDULO de carga.
 *
 * Los componentes del asistente (AssistantChat, DraftCard, DraftList) son
 * genéricos: todo lo propio de un módulo vive acá. Para sumar uno nuevo
 * (evidencias del plan de acción, planillas, proveedores…) se agrega una
 * entrada en ASSISTANT_MODULES con:
 *   - id               el `module` que viaja al API
 *   - minTier          plan mínimo para subir documentos (el chat no pide plan)
 *   - routePrefixes    rutas donde ese módulo es el contexto de la pantalla
 *   - kinds            por `kind`: ícono, layout ('record' | 'sheet') y, en los
 *                      'record', qué mostrar de `reading`/`target`, claves del
 *                      PATCH que difieren de `question.field` y el formulario
 *                      de "Corregir" (el título sale de i18n)
 *   - settingsRoute    dónde se crean las entidades que falten
 *
 * Desde cualquier pantalla: `useAssistantDock().open({ attach: true, module: 'monitor' })`.
 */

const FUELS = ['Diesel', 'Gasoline', 'LPG', 'Biodiesel'];

// ── Perfiles por tipo de documento ──────────────────────────────────
// layout 'record': un registro (recibo, capacitación) → tarjeta con datos,
//                  destino y "Corregir".
// layout 'sheet':  una planilla con muchas filas (Excel/CSV) → vista previa en
//                  tabla (DraftSheet). Sus preguntas y PATCH usan `question.field`
//                  tal cual (`inventory_value:Oficina`, `group:g3:source`…).
//
// patchKeys: `question.field` → clave del PATCH cuando NO es la misma. Lo que no
// está acá se manda con `question.field` como clave. El control lo dice
// `question.input` (choice · number · date · text).

const RECEIPT = {
  layout: 'record',
  readingFields: [
    { key: 'date', type: 'date' },
    { key: 'period', type: 'period', from: 'period_start', to: 'period_end' },
    { key: 'consumption', type: 'quantity', unitFrom: 'unit' },
    { key: 'fuel' },
    { key: 'identifier' },
    { key: 'plate' },
    { key: 'provider' },
    { key: 'cost', type: 'money', currencyFrom: 'currency' },
  ],
  targetFields: [
    { key: 'inventoryId', label: 'inventory', options: 'inventories' },
    { key: 'activity_id', label: 'activity', options: 'activities' },
    { key: 'sourceId', label: 'source', options: 'sources' },
    { key: 'vehicleActivityId', label: 'vehicle', options: 'vehicles' },
    { key: 'water_source', label: 'water_source', options: 'water_sources' },
  ],
  patchKeys: {
    inventory: { key: 'inventoryId' },
    source: { key: 'sourceId' },
    vehicle: { key: 'vehicleActivityId' },
    activity: { key: 'activity_id' },
    fuel: { key: 'fuel', fallbackOptions: FUELS },
  },
  editFields: [
    { key: 'date', label: 'date', input: 'date', from: 'reading' },
    { key: 'consumption', label: 'consumption', input: 'number', from: 'reading', unitFrom: 'unit' },
    { key: 'fuel', label: 'fuel', from: 'reading', static: FUELS, onlyKind: 'fuel' },
    { key: 'inventoryId', label: 'inventory', from: 'target', options: 'inventories', number: true },
    { key: 'activity_id', label: 'activity', from: 'target', options: 'activities', number: true, filterBy: { optionKey: 'inventory_id', field: 'inventoryId' } },
    { key: 'sourceId', label: 'source', from: 'target', options: 'sources', number: true },
    { key: 'vehicleActivityId', label: 'vehicle', from: 'target', options: 'vehicles', number: true, onlyWhen: { field: 'sourceId', options: 'sources', optionKey: 'code', equals: 'ownedVehicles' } },
    { key: 'water_source', label: 'water_source', from: 'target', options: 'water_sources', onlyKind: 'water' },
  ],
};

// Lista de asistencia a capacitación (§ 4, kind: 'training'). Pide Cohort al confirmar.
const TRAINING = {
  layout: 'record',
  readingFields: [
    { key: 'name' },
    { key: 'topic', optionsFrom: 'topics' },
    { key: 'date', type: 'date' },
    { key: 'totalStaffTrained', type: 'number' },
    { key: 'totalInvited', type: 'number' },
    { key: 'totalHourTraining', type: 'hours' },
    { key: 'trainingMode', valueKey: 'trainingMode' },
    { key: 'department' },
  ],
  targetFields: [
    { key: 'inventoryId', label: 'inventory', options: 'inventories' },
    { key: 'form', label: 'form', valueKey: 'form', from: 'reading' },
  ],
  patchKeys: {
    inventory: { key: 'inventoryId' },
  },
  editFields: [
    { key: 'name', label: 'name', input: 'text', from: 'reading' },
    { key: 'date', label: 'date', input: 'date', from: 'reading' },
    { key: 'totalStaffTrained', label: 'totalStaffTrained', input: 'number', from: 'reading' },
    { key: 'totalInvited', label: 'totalInvited', input: 'number', from: 'reading' },
    { key: 'totalHourTraining', label: 'totalHourTraining', input: 'number', from: 'reading' },
    { key: 'inventoryId', label: 'inventory', from: 'target', options: 'inventories', number: true },
  ],
};

const SHEET = { layout: 'sheet' };

export const ASSISTANT_MODULES = {
  monitor: {
    id: 'monitor',
    minTier: 'launchpad',
    routePrefixes: ['/monitor'],
    icon: markRaw(PhReceipt),
    kinds: {
      electricity: { icon: markRaw(PhLightning), ...RECEIPT },
      fuel: { icon: markRaw(PhGasPump), ...RECEIPT },
      water: { icon: markRaw(PhDrop), ...RECEIPT },
      training: { icon: markRaw(PhChalkboardTeacher), ...TRAINING },
      staff_roster: { icon: markRaw(PhUsersThree), ...SHEET },
      consumption_log: { icon: markRaw(PhTable), ...SHEET },
    },
    settingsRoute: { name: 'MonitorRoute', query: { section: 'setting' } },
  },
};

// Módulo para subir cuando la pantalla no tiene uno propio (hoy, el único).
export const DEFAULT_UPLOAD_MODULE = 'monitor';

export const moduleConfig = (id) => ASSISTANT_MODULES[id] || null;
// Tipo desconocido → tarjeta genérica de un registro, sin campos propios.
export const kindConfig = (module, kind) =>
  ASSISTANT_MODULES[module]?.kinds?.[kind] || { icon: markRaw(PhFileText), layout: 'record', readingFields: [], targetFields: [], patchKeys: {}, editFields: [] };
export const kindIcon = (module, kind) => kindConfig(module, kind).icon;
export const moduleForPath = (path = '') =>
  Object.values(ASSISTANT_MODULES).find(m => m.routePrefixes?.some(p => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}-`)))?.id || null;

// Visible solo con la marca activa (en prueba): ?assistant=1 la prende en ese
// navegador, ?assistant=0 la apaga. La lee App.vue (el dock) y quien lo abra.
const ASSISTANT_FLAG = 'gme.assistant.enabled';
const readFlag = () => { try { return localStorage.getItem(ASSISTANT_FLAG) === '1'; } catch { return false; } };
const enabled = ref(readFlag());
export const setAssistantEnabled = (on) => {
  try { if (on) localStorage.setItem(ASSISTANT_FLAG, '1'); else localStorage.removeItem(ASSISTANT_FLAG); } catch { /* storage bloqueado */ }
  enabled.value = !!on;
};
export const assistantEnabled = readonly(enabled);

// Estado compartido: un solo dock por app.
const isOpen = ref(false);
const view = ref('chat');       // 'chat' | 'pending'
const intent = ref(null);       // { id, attach?, module?, pending? } — lo consume el chat

let seq = 0;

export function useAssistantDock() {
  const open = (options = {}) => {
    isOpen.value = true;
    view.value = options.pending ? 'pending' : 'chat';
    if (Object.keys(options).length) intent.value = { id: ++seq, ...options };
  };
  const close = () => { isOpen.value = false; };
  const toggle = () => { isOpen.value = !isOpen.value; };
  const setView = (v) => { view.value = v; };
  const consumeIntent = () => { const i = intent.value; intent.value = null; return i; };

  return {
    isOpen: readonly(isOpen),
    view: readonly(view),
    intent: readonly(intent),
    open, close, toggle, setView, consumeIntent,
  };
}
